// Rendu du labyrinthe sur le canvas (résolution interne = taille CSS × devicePixelRatio)
import { ratPawPrints } from '../core/chase.js';
import { fadePrints } from '../core/trail.js';

export function createRenderer(canvas, container, theme) {
  const ctx = canvas.getContext('2d');

  // --- Sprites -------------------------------------------------------------
  // Un sprite SVG par emoji qui en a un (theme.sprites), chargé une fois, à sa première apparition ;
  // tant qu'il n'est pas prêt, ou pour les emojis sans sprite, on dessine l'emoji texte.
  const images = new Map();
  const readyCallbacks = [];

  function loadSprite(src) {
    if (!src || typeof Image === 'undefined') return null;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => readyCallbacks.forEach((cb) => cb());
    img.src = src;
    return img;
  }

  /** Dessin d'un emoji : son image (ou null), sa taille relative et son sens. */
  function spriteOf(emoji) {
    const def = theme.sprites?.[emoji];
    if (def && !images.has(emoji)) images.set(emoji, loadSprite(def.src));
    return { emoji, img: images.get(emoji) ?? null, scale: def?.scale ?? 1, facesLeft: def?.facesLeft ?? false };
  }

  /** Appelle `cb` à chaque sprite chargé, pour redessiner sans attendre la prochaine frame. */
  function onAssetsLoaded(cb) {
    readyCallbacks.push(cb);
  }

  /** Ajuste la résolution du canvas à sa taille CSS. Retourne false si le conteneur n'est pas mesurable. */
  function resize() {
    const cssWidth = container.clientWidth;
    if (cssWidth <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.round(cssWidth * dpr);
    if (canvas.width !== px) {
      canvas.width = px;
      canvas.height = px;
    }
    return true;
  }

  /**
   * Empreintes de pattes (voir core/chase.js pour le rat, core/trail.js pour la souris) : un
   * coussinet et quatre doigts tournés dans le sens de marche, les plus anciennes s'estompant.
   * Petites et fines, proportionnelles à la case, avec un minimum pour rester visibles.
   */
  function drawPawPrints(prints, color, cellSize) {
    if (prints.length === 0) return;
    const unit = cellSize;
    const toeRadius = Math.max(0.8, unit * 0.022);
    ctx.save();
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 4;
    for (const p of prints) {
      ctx.save();
      ctx.globalAlpha = 0.9 - 0.55 * p.age;
      ctx.translate(p.x * cellSize, p.y * cellSize);
      ctx.rotate(p.angle); // +x = sens de marche
      ctx.beginPath();
      ctx.ellipse(0, 0, Math.max(1, unit * 0.052), Math.max(0.9, unit * 0.04), 0, 0, Math.PI * 2);
      ctx.fill();
      for (const [tx, ty] of [[0.075, -0.05], [0.098, -0.018], [0.098, 0.018], [0.075, 0.05]]) {
        ctx.beginPath();
        ctx.arc(tx * unit, ty * unit, toeRadius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.restore();
  }

  /** Traces de l'adversaire le long de sa trace (core/scenarios/common.js) : rien sous son corps ni collé à lui. */
  function drawRivalPrints(s, cellSize) {
    const { trail, rival } = s.scene;
    if (!trail || trail.length < 2) return;
    const { scale } = spriteOf(s.cast.rival);
    // ratPawPrints part du bout du chemin (dernier index) vers l'adversaire (index 0)
    const prints = ratPawPrints([...trail].reverse(), 0, { ratPos: rival, clearance: scale * 0.72 * 0.85 });
    drawPawPrints(prints, theme.pathColor, cellSize);
  }

  /** Traces de la souris, le long de son vrai trajet (libre au doigt) : rien sous elle. */
  function drawMousePrints(s, cellSize) {
    if (!s.mouseTrail) return;
    const view = s.playerView ?? s.player;
    const prints = fadePrints(s.mouseTrail.prints, { x: view.x + 0.5, y: view.y + 0.5 }, 0.72 * 0.75);
    drawPawPrints(prints, theme.mousePrintColor, cellSize);
  }

  function drawWalls(grid, cellSize, color = theme.wallColor) {
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, cellSize * 0.12);
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;

    ctx.beginPath();
    for (const row of grid) {
      for (const cell of row) {
        const px = cell.x * cellSize;
        const py = cell.y * cellSize;
        if (cell.walls.top) { ctx.moveTo(px, py); ctx.lineTo(px + cellSize, py); }
        if (cell.walls.right) { ctx.moveTo(px + cellSize, py); ctx.lineTo(px + cellSize, py + cellSize); }
        if (cell.walls.bottom) { ctx.moveTo(px, py + cellSize); ctx.lineTo(px + cellSize, py + cellSize); }
        if (cell.walls.left) { ctx.moveTo(px, py); ctx.lineTo(px, py + cellSize); }
      }
    }
    ctx.stroke();
  }

  /**
   * Halo lumineux autour de la souris pendant qu'on la fait glisser au doigt : sur téléphone,
   * le doigt cache le petit sprite, ce halo largement plus grand que la case reste visible tout
   * autour et confirme que le geste est bien pris en compte. Légère respiration pour ne pas
   * paraître figé, sans être distrayante. Sert aussi à désigner le personnage d'une bulle d'aide.
   */
  function drawGlow(pos, cellSize, color) {
    const cx = pos.x * cellSize + cellSize / 2;
    const cy = pos.y * cellSize + cellSize / 2;
    const pulse = 0.12 * Math.sin(performance.now() / 220);
    const radius = cellSize * (1.35 + pulse);

    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    gradient.addColorStop(0, `${color}b3`);
    gradient.addColorStop(0.6, `${color}40`);
    gradient.addColorStop(1, `${color}00`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * @param {{emoji:string, img:HTMLImageElement|null, scale:number}} sprite voir spriteOf
   * @param {number} [scale=1] taille relative en plus de celle du sprite (action entamée)
   * @param {boolean} [mirror=false] retourné horizontalement (adversaire allant vers la droite)
   */
  function drawSprite({ emoji, img, scale: spriteScale }, pos, cellSize, { scale: extraScale = 1, mirror = false } = {}) {
    const scale = spriteScale * extraScale;
    const cx = pos.x * cellSize + cellSize / 2;
    const cy = pos.y * cellSize + cellSize / 2;
    ctx.save();
    ctx.translate(cx, cy);
    if (mirror) ctx.scale(-1, 1);
    const paint = () => {
      if (img && img.complete && img.naturalWidth > 0) {
        const size = cellSize * 0.72 * scale;
        ctx.drawImage(img, -size / 2, -size / 2, size, size);
      } else {
        ctx.font = `${cellSize * 0.65 * scale}px sans-serif`;
        ctx.fillText(emoji, 0, cellSize * 0.05 * scale);
      }
    };
    // Emoji sombre (🎶) : détouré d'un halo clair, en deux passes (un liseré net, puis une lueur), pour se voir sur le fond
    if (theme.haloEmoji?.includes(emoji)) {
      ctx.shadowColor = theme.emojiHaloColor;
      for (const blur of [0.05, 0.16]) {
        ctx.shadowBlur = cellSize * blur * scale;
        paint();
      }
      ctx.shadowBlur = 0;
    }
    paint();
    ctx.restore();
  }

  /** Cases vues par l'adversaire (cache-cache) : un voile jaune, plus pâle en s'éloignant. */
  function drawSight(sight, cellSize) {
    sight.forEach((cell, i) => {
      ctx.fillStyle = theme.sightColor;
      ctx.globalAlpha = 0.32 * (1 - i / (sight.length + 1));
      ctx.fillRect(cell.x * cellSize, cell.y * cellSize, cellSize, cellSize);
    });
    ctx.globalAlpha = 1;
  }

  /** Brouillard (dans le noir) : seul un disque autour du joueur reste visible, bord adouci. */
  function drawFog(view, radius, cellSize) {
    const cx = (view.x + 0.5) * cellSize;
    const cy = (view.y + 0.5) * cellSize;
    const gradient = ctx.createRadialGradient(cx, cy, radius * cellSize * 0.55, cx, cy, radius * cellSize);
    gradient.addColorStop(0, `${theme.canvasBg}00`);
    gradient.addColorStop(1, theme.canvasBg);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  /** Petit emoji de l'action en haut à droite d'un personnage (cœur qui se vide, gant ramassé). */
  function drawBadge(s, pos, cellSize, ratio) {
    if (ratio == null || ratio <= 0) return;
    drawSprite(spriteOf(s.cast.action.emoji), { x: pos.x + 0.35, y: pos.y - 0.35 }, cellSize, { scale: 0.5 * Math.sqrt(ratio) });
  }

  /** Statues musicales : grande main au centre pendant l'arrêt, pâle et pulsée pendant l'annonce. */
  function drawFreezeSign(phase) {
    if (phase !== 'freeze' && phase !== 'warn') return;
    ctx.save();
    ctx.globalAlpha = phase === 'freeze' ? 0.35 : 0.15 + 0.1 * Math.sin(performance.now() / 90);
    ctx.font = `${canvas.width * 0.45}px sans-serif`;
    ctx.fillText('✋', canvas.width / 2, canvas.height / 2);
    ctx.restore();
  }

  /**
   * Adversaire : il clignote avant de disparaître (bisou volé) ; dans le brouillard, il n'est visible
   * que de près ou quand il appelle. Un sprite qui regarde à gauche est retourné quand il va à droite.
   */
  // Opacité de l'adversaire : quand il va disparaître (`blink`, bisou volé), il s'efface en fondu doux, puis
  // réapparaît de la même façon ; l'opacité se rapproche de sa cible à chaque image (courbe qui ralentit,
  // constante de temps FADE_MS), sans jamais osciller.
  const FADE_MS = 350;
  let rivalOpacity = 1;
  let rivalOpacityAt = 0;
  function nextRivalOpacity(fading, instant) {
    const now = performance.now();
    const dt = Math.min(100, now - rivalOpacityAt); // écart plafonné : après un onglet en arrière-plan, pas de saut
    rivalOpacityAt = now;
    if (instant) rivalOpacity = 1;
    else rivalOpacity += ((fading ? 0 : 1) - rivalOpacity) * (1 - Math.exp(-dt / FADE_MS));
    return rivalOpacity;
  }

  /** @param {{instant?: boolean}} [options] instant : pleine opacité tout de suite (image figée de fin de niveau) */
  function drawRival(s, cellSize, view, { instant = false } = {}) {
    const { rival, heading, blink, fog, ping } = s.scene;
    const pointed = s.hintCell && s.hintCell.x === rival.x && s.hintCell.y === rival.y; // bulle d'aide
    const hidden = fog != null && !ping && !pointed && Math.hypot(rival.x - view.x, rival.y - view.y) > fog;
    if (hidden) return;
    const sprite = spriteOf(s.cast.rival);
    // Début de niveau (aucun tic) : toujours pleinement visible, quoi qu'ait fait l'adversaire du niveau d'avant
    const opacity = nextRivalOpacity(blink, instant || s.scene.ticks === 0);
    if (opacity < 0.02) return; // effacé
    ctx.save();
    ctx.globalAlpha = opacity;
    // Pendant la danse, l'adversaire se dandine
    if (s.scene.phase === 'music') {
      const sway = Math.sin(performance.now() / 160) * 0.08;
      drawSprite(sprite, { x: rival.x + sway, y: rival.y - Math.abs(sway) }, cellSize, { mirror: sprite.facesLeft && heading > 0 });
    } else {
      drawSprite(sprite, rival, cellSize, { mirror: sprite.facesLeft && heading > 0 });
    }
    ctx.restore();
    drawBadge(s, rival, cellSize, s.scene.rivalBadge);
  }

  /** Personnages et objets du scénario (core/scenarios/common.js), puis le joueur. */
  function drawScene(s, cellSize, { finished }) {
    const { scene } = s;
    const view = s.playerView ?? s.player;
    const action = spriteOf(s.cast.action.emoji);
    if (!finished) {
      if (s.hintCell) drawGlow(s.hintCell, cellSize, theme.hintGlowColor);
      for (const item of scene.items) drawSprite(action, item, cellSize, { scale: 0.75 });
      if (scene.goal) drawSprite(action, scene.goal, cellSize, { scale: Math.sqrt(scene.goalScale) });
      if (scene.fog != null) drawFog(view, scene.fog, cellSize);
    }
    drawRival(finished ? { ...s, scene: { ...scene, fog: null, blink: false } } : s, cellSize, view, { instant: finished });
    if (!finished && s.isTouching) drawGlow(view, cellSize, theme.touchGlowColor);
    drawSprite(spriteOf(s.cast.player), view, cellSize);
    drawBadge(s, view, cellSize, scene.playerBadge);
  }

  /**
   * @param {object} s état de rendu
   * @param {object[][]} s.grid
   * @param {{x,y}[]} s.optimalPath
   * @param {object|null} s.scene état du scénario de l'action (core/scenarios/common.js), null avant le tirage
   * @param {{player:string, rival:string, action:{emoji:string}}} s.cast distribution tirée au bandit manchot
   * @param {{x,y}} s.player
   * @param {{x,y}} [s.playerView] position affichée de la souris (fractionnaire pendant un glissement)
   * @param {{prints: object[]}} [s.mouseTrail] traces de pattes de la souris (core/trail.js)
   * @param {string} s.state
   * @param {boolean} [s.isTouching] glissement tactile en cours (halo autour de la souris)
   * @param {{x,y}|null} [s.hintCell] personnage désigné par la bulle d'aide (halo)
   */
  function draw(s) {
    const gridSize = s.grid.length;
    if (gridSize === 0) return;
    const cellSize = canvas.width / gridSize;

    ctx.fillStyle = theme.canvasBg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const { scene } = s;
    if (scene) {
      drawRivalPrints(s, cellSize);
      if (s.state === 'playing') drawSight(scene.sight, cellSize);
    }
    drawMousePrints(s, cellSize);
    // Statues musicales : murs orange à l'annonce, rouges pendant l'arrêt
    const wallColor = { warn: theme.warnWallColor, freeze: theme.freezeWallColor }[scene?.phase] ?? theme.wallColor;
    drawWalls(s.grid, cellSize, s.state === 'playing' ? wallColor : theme.wallColor);

    // Pas d'ombre sur les sprites (sinon flous)
    ctx.shadowBlur = 0;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Avant le tirage, le labyrinthe seul ; en fin de niveau, sans brouillard ni objets
    if (!scene) return;
    if (s.state === 'playing') drawFreezeSign(scene.phase);
    drawScene(s, cellSize, { finished: s.state === 'finished' });
  }

  return { resize, draw, onAssetsLoaded };
}
